import { useState, useMemo } from 'react';
import { Order } from '../types';
import { fetchOrders } from '../utils/orderApi';

type SortKey = 'id' | 'date_created' | 'date_completed' | 'customer_name' | 'status' | 'total';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  key: SortKey;
  order: SortOrder;
}

const useOrdersTable = (orders: Order[], page: number, perPage: number = 200) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'id', order: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      const newOrder = prev.key === key && prev.order === 'asc' ? 'desc' : 'asc';
      return { key, order: newOrder };
    });
  };

  const sortedAndFilteredData = useMemo(() => {
    let data = [...orders];

    if (sortConfig.key) {
      data = data.sort((a, b) => {
        const { key, order } = sortConfig;
        let aValue: string | number = a[key] ?? '';
        let bValue: string | number = b[key] ?? '';

        if (key === 'id' || key === 'total') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        } else if (key === 'customer_name' || key === 'status') {
          aValue = String(aValue);
          bValue = String(bValue);
        } else if (key === 'date_created' || key === 'date_completed') {
          const aDate = typeof aValue === 'string' && aValue ? new Date(aValue) : new Date(0);
          const bDate = typeof bValue === 'string' && bValue ? new Date(bValue) : new Date(0);
          aValue = aDate.getTime();
          bValue = bDate.getTime();
        }

        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      data = data.filter((order) =>
        String(order.id).toLowerCase().includes(lowerSearchTerm) ||
        (order.customer_name?.toLowerCase().includes(lowerSearchTerm) ?? false) ||
        (order.status?.toLowerCase().includes(lowerSearchTerm) ?? false) ||
        (order.customer_type?.toLowerCase().includes(lowerSearchTerm) ?? false) ||
        String(order.total).toLowerCase().includes(lowerSearchTerm) ||
        order.items.some((item) =>
          item.product_name.toLowerCase().includes(lowerSearchTerm)
        ) ||
        order.shipments.some((shipment) =>
          shipment.tracking_number.toLowerCase().includes(lowerSearchTerm) ||
          shipment.carrier.toLowerCase().includes(lowerSearchTerm)
        )
      );
    }

    return data;
  }, [orders, sortConfig.key, sortConfig.order, searchTerm]);

  const displayedData = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedAndFilteredData.slice(start, start + perPage);
  }, [sortedAndFilteredData, page, perPage]);

  const totalPages = Math.ceil(sortedAndFilteredData.length / perPage);

  return {
    displayedData,
    totalPages,
    sortConfig,
    searchTerm,
    setSearchTerm,
    handleSort,
  };
};

export default useOrdersTable;